import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import MenuPanel from './MenuPanel';

export default function NavGroup({ label, links }: { label: string; links: { to: string; label: string }[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const triggerRef = useRef<HTMLAnchorElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const active = links.some((l) => pathname === l.to || pathname.startsWith(`${l.to}/`));

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="navgroup" ref={wrapRef}>
      <a
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className={`navgroup-trigger${active ? ' active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
      >
        {label}<span className="usermenu-caret" aria-hidden="true">▾</span>
      </a>
      {open && (
        <MenuPanel anchor={triggerRef} panelRef={panelRef} align="start" id={panelId} className="usermenu-panel navgroup-panel" onClick={() => setOpen(false)}>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} role="menuitem" className={({ isActive }) => `usermenu-item${isActive ? ' active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </MenuPanel>
      )}
    </div>
  );
}
